import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { capitalize } from "lodash-es";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import { createDateAxis, createValueAxis } from "../../Trends/TrendsUtils";
import useTrend from "../../Trends/useTrend";

export interface FindingsByDataSourceProps {
  selectedScope?: string | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  timeScale: string;
  widgetKey?: string;
}

// One point on a series, keyed by data-source category.
interface TrendPoint {
  date: string;
  value: number;
}

// Map of data-source category -> series points, built from the raw trend rows.
type TrendDataByCategory = Record<string, TrendPoint[]>;

// Raw trend rows from useTrend: [date, category, value] tuples.
type TrendRow = [string, string, number];

const FindingsByDataSource: React.FC<FindingsByDataSourceProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  timeScale,
  widgetKey,
}) => {
  const {
    trendData: data,
    trendDataError,
    trendLoading,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      selection: ["open"],
      timescale: [timeScale],
      group_by: ["source"],
      aggregate_functions: ["SUM"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope,
        filters_fields_to_exclude: ["actual_status"],
      },
    },
    skip: (!scopeToExecute && !selectedScope) || !filterToExecute,
  });
  const [trendData, setTrendData] = useState<TrendDataByCategory | null>(null);
  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart();

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      setTrendData({});
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      const rows = data as TrendRow[];
      const dataObj: TrendDataByCategory = {};

      for (let i = 0; i < rows.length; i += 1) {
        const category = rows[i][1];
        if (!dataObj.hasOwnProperty(category)) {
          dataObj[category] = [];
        }

        dataObj[category].push({
          date: rows[i][0],
          value: rows[i][2],
        });
      }

      if (Object.keys(dataObj)?.length === 0) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (Object.keys(dataObj)?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      setTrendData(dataObj);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(
        "FindingsByDataSourceTrend",
        am4charts.XYChart,
        {
          isScrollable: true,
        }
      );

      chart.colors.list = [
        am4core.color("#41425a"),
        am4core.color("#607aff"),
        am4core.color("#b3bbf7"),
        am4core.color("#230051"),
      ];

      chart.paddingTop = 30;
      chart.paddingRight = 40;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Findings");
      valueAxis.getSeriesDataItem = function (
        this: am4charts.ValueAxis,
        series,
        position
      ) {
        const key = this.axisFieldName + this.axisLetter;
        const value = this.positionToValue(position);
        const dataItem = series.dataItems.getIndex(
          series.dataItems.findClosestIndex(
            value,
            (x) => {
              return (x as unknown as Record<string, number>)[key]
                ? (x as unknown as Record<string, number>)[key]
                : (undefined as unknown as number);
            },
            "any"
          )
        );
        return dataItem;
      } as typeof valueAxis.getSeriesDataItem;

      if (trendData) {
        for (const [key] of Object.entries(trendData)) {
          createSeries(key, capitalize(key));
        }
      }

      function createSeries(s: string, name: string) {
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "value";
        series.dataFields.dateX = "date";
        series.name = name;
        series.tensionX = 0.8;
        series.strokeWidth = 3;
        series.strokeOpacity = 0.6;
        series.tooltipText = "{name}: {value}";
        series.tooltip!.autoTextColor = false;

        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.strokeWidth = 2;
        bullet.circle.radius = 4;

        let segment = series.segments.template;
        segment.interactionsEnabled = true;

        segment.events.on("over", function (event) {
          processOver(event.target.parent!.parent!.parent);
        });

        segment.events.on("out", function (event) {
          processOut();
        });

        series.data = trendData ? trendData[s] : [];

        return series;
      }

      chart.legend.itemContainers.template.events.on("over", function (event) {
        processOver(event.target.dataItem!.dataContext);
      });

      chart.legend.itemContainers.template.events.on("out", function (event) {
        processOut();
      });

      function processOver(hoveredSeries: unknown) {
        const series = hoveredSeries as am4charts.LineSeries;
        series.toFront();

        series.segments.each(function (segment) {
          segment.setState("hover");
        });

        chart.series.each(function (s) {
          if (s !== series) {
            (s as am4charts.LineSeries).segments.each(function (segment) {
              segment.setState("dimmed");
            });
            (s as am4charts.LineSeries).bulletsContainer.setState("dimmed");
          }
        });
      }

      function processOut() {
        chart.series.each(function (series) {
          (series as am4charts.LineSeries).segments.each(function (segment) {
            segment.setState("default");
          });
          (series as am4charts.LineSeries).bulletsContainer.setState("default");
        });
      }

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [trendData, dataExists, createChart]);
  return (
    <Chart
      chartLoading={chartLoading}
      chartName={"FindingsByDataSourceTrend"}
      chartError={dataError}
      chartData={dataExists}
    />
  );
};

export default FindingsByDataSource;
